<script>
'use strict';

const auth = require('../../../../services/client/auth');
const {
  wishlistProduct,
  removeWishlistedProduct,
} = require('../../../../services/client/voxstar');
const gtm = require('../../../../services/client/gtm');
const analytics = require('../../lib/analytics');

const ANIMATION_CLASSES = [
  'red-animation',
  'pink-animation',
  'green-animation',
  'orange-animation',
];

module.exports = {
  name: 'ProductCard',
  props: {
    index: Number,
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isSaving: false,
      animationClass: '',
      showNotification: false,
      notificationActive: false,
    };
  },
  computed: {
    product() {
      return this.item.product;
    },
    isSaved() {
      return this.$store.state.savedProductIDs.includes(this.product.productId);
    },
    savedForLaterLink() {
      return this.$store.state.savedForLaterLink;
    },
    productData() {
      return {
        productId: this.product.productId,
        retailerName: this.product.topMerchant.retailerName,
        price: this.product.topMerchant.price,
        salePrice: this.product.topMerchant.salePrice || null,
        pageUri: this.$store.state.pageUri,
        canonicalUrl: window.location.href,
        merchantId: this.product.topMerchant.id,
        name: this.product.name,
        component: 'Search',
        headline: this.$store.state.searchValue,
      };
    },
    readStoryUrl() {
      const { activeTab, searchValue } = this.$store.state;

      return analytics.buildArticleUrl(
        this.product.canonicalUrl,
        activeTab,
        searchValue
      );
    },
  },
  methods: {
    handleSaveClick() {
      if (!auth.isAuthenticated()) {
        window.dispatchEvent(
          new CustomEvent('auth-modal:show', {
            detail: {
              context: 'save-product',
              isSaved: !this.isSaved,
              data: Object.assign({}, this.productData, {
                cookie: {
                  name: 'voxstar_saved_product',
                  props: Object.keys(this.productData),
                },
              }),
            },
          })
        );
        return;
      }

      const saveMsg =
        'There was an error saving this product. Please try again later.';
      const unsaveMsg =
        'There was an error unsaving this product. Please try again later.';

      if (!this.isSaved) {
        this.$store.commit('addSavedProductID', this.product.productId);
        wishlistProduct(this.productData)
          .then((res) => {
            if (res?.status !== 201) {
              this.$store.commit(
                'removeSavedProductID',
                this.product.productId
              );
              this.$store.dispatch('showErrorMsg', { message: saveMsg });
              return;
            }
            this.triggerSaveAnimation();
            auth.productSaveEvent('product_save', this.productData);
            // TODO: sailthru save event
          })
          .catch(() => {
            this.$store.commit('removeSavedProductID', this.product.productId);
            this.$store.dispatch('showErrorMsg', { message: saveMsg });
          });
      } else {
        this.$store.commit('removeSavedProductID', this.product.productId);
        removeWishlistedProduct(this.product.productId)
          .then((res) => {
            if (res?.status !== 204) {
              this.$store.commit('addSavedProductID', this.product.productId);
              this.$store.dispatch('showErrorMsg', { message: unsaveMsg });
              return;
            }
            auth.productSaveEvent('product_unsave', this.productData);
            // TODO: sailthru unsave event
          })
          .catch(() => {
            this.$store.commit('addSavedProductID', this.product.productId);
            this.$store.dispatch('showErrorMsg', { message: unsaveMsg });
          });
      }
    },
    triggerSaveAnimation() {
      const randomIndex = Math.floor(Math.random() * ANIMATION_CLASSES.length);

      this.isSaving = true;
      this.animationClass = ANIMATION_CLASSES[randomIndex];
      this.$refs.saveButton.addEventListener(
        'animationend',
        () => {
          this.isSaving = false;
          this.animationClass = '';
          this.triggerSaveNotification();
        },
        { once: true }
      );
    },
    triggerSaveNotification() {
      this.showNotification = true;
      this.notificationActive = true;
      this.$refs.saveNotification.addEventListener(
        'animationend',
        () => {
          this.showNotification = false;
          this.notificationActive = false;
        },
        { once: true }
      );
    },
    onProductVisible() {
      const { activeTab, productFilters, searchValue } = this.$store.state;

      analytics.fireViewItemEvent({
        product: this.product,
        filters: productFilters,
        activeTab,
        searchValue,
        position: this.index + 1,
      });
    },
    onImageClick() {
      const { activeTab, searchValue, productFilters } = this.$store.state;

      analytics.fireProductClickEvent({
        product: this.product,
        clickTarget: 'image',
        activeTab,
        position: this.index + 1,
        searchValue,
        filters: productFilters,
      });
    },
    onNameClick() {
      const { activeTab, searchValue, productFilters } = this.$store.state;

      analytics.fireProductClickEvent({
        product: this.product,
        clickTarget: 'name',
        activeTab,
        position: this.index + 1,
        searchValue,
        filters: productFilters,
      });
    },
    onBuyClick() {
      const { activeTab, searchValue, productFilters } = this.$store.state;

      analytics.fireProductClickEvent({
        product: this.product,
        clickTarget: 'button',
        activeTab,
        position: this.index + 1,
        searchValue,
        filters: productFilters,
      });
    },
    onReadStoryClick() {
      const { activeTab, searchValue, productFilters } = this.$store.state;

      gtm.reportNow({
        event: 'article_click',
        article_click: {
          product_search_query: searchValue,
          product_search_budget: analytics.formatBudget(productFilters),
          product_search_variation: analytics.formatVariation(productFilters),
          component_name: 'augmented-search-results',
          component_type: analytics.formatTabName(activeTab),
          click_text: 'read story',
          click_uri: this.product.canonicalUrl,
          position: this.index + 1,
        },
      });
    },
  },
};
</script>

<template>
  <div class="product-card">
    <div class="contents">
      <div class="image-section">
        <template v-for="badge in product.badges">
          <div
            class="product-badge"
            :style="{ transform: `rotate(${badge.rotation})` }"
          >
            <img class="badge-img" :src="badge.image" :alt="badge.name" />
          </div>
        </template>
        <div
          class="product-save-section"
          :class="[{ saved: isSaved, saving: isSaving }, animationClass]"
          ref="saveButton"
          @click="handleSaveClick"
        >
          <div class="save-icon-wrapper">
            <span class="save-heart-icon"></span>
          </div>
          <div class="save-animation-wrapper">
            <!--
              Inlined SVG: CSS must target .product-saving-big-heart and .product-saving-small-heart
              directly for staggered animations, translateY starting position, and color variants to work.
              <img> creates a CSS isolation boundary that blocks all external style rules.
              Revisit once fully migrated to Vite — require/import of SVG files will be possible then.
            -->
            <svg
              width="78"
              height="74"
              viewBox="0 0 78 74"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <g
                class="product-saving-big-heart"
                clip-path="url(#clip0_3216_10821)"
              >
                <g>
                  <path
                    d="M40.262 53.5401L37.4255 52.2734C32.521 50.0423 28.4298 48.0694 25.152 46.3546C21.8742 44.6398 19.1826 43.0303 17.0772 41.526C14.9718 40.0217 13.3641 38.5628 12.2542 37.149C11.1443 35.7353 10.3228 34.2082 9.78972 32.5676C8.74678 29.3578 8.95363 26.3235 10.4103 23.4647C11.8669 20.6059 14.1823 18.6609 17.3564 17.6295C19.3893 16.969 21.4271 16.8392 23.4697 17.2401C25.5123 17.641 27.4711 18.5424 29.346 19.9441C30.2181 17.5315 31.3446 15.5981 32.7253 14.1441C34.1061 12.69 35.7594 11.65 37.6853 11.0243C40.8594 9.99294 43.8759 10.2056 46.7347 11.6622C49.5935 13.1188 51.5443 15.4521 52.5873 18.6619C53.1203 20.3024 53.3533 22.0208 53.2863 23.8169C53.2193 25.613 52.7763 27.7383 51.9572 30.1928C51.1381 32.6473 49.9066 35.5315 48.2627 38.8455C46.6188 42.1594 44.4686 46.1603 41.8122 50.8481L40.262 53.5401Z"
                    fill="#F85D00"
                  />
                </g>
              </g>
              <g
                class="product-saving-small-heart"
                clip-path="url(#clip1_3216_10821)"
              >
                <g>
                  <path
                    d="M50.2953 64.3526L48.9448 62.4895C46.6249 59.2417 44.7311 56.4608 43.2635 54.1468C41.7959 51.8328 40.6653 49.8034 39.8719 48.0586C39.0784 46.3138 38.5875 44.7825 38.399 43.4645C38.2106 42.1465 38.2492 40.8626 38.5149 39.6127C39.0347 37.1674 40.2912 35.2975 42.2844 34.003C44.2776 32.7086 46.4834 32.3184 48.9016 32.8324C50.4503 33.1616 51.8056 33.8331 52.9674 34.8468C54.1293 35.8605 55.052 37.1642 55.7356 38.7578C57.1887 37.5331 58.6273 36.7101 60.0516 36.2887C61.4759 35.8673 62.9217 35.8125 64.3889 36.1243C66.8071 36.6383 68.6634 37.892 69.9578 39.8852C71.2523 41.8784 71.6396 44.0978 71.1198 46.5431C70.8541 47.793 70.3672 48.9816 69.659 50.109C68.9508 51.2364 67.8794 52.4357 66.4449 53.7069C65.0104 54.9781 63.1521 56.3722 60.8702 57.8892C58.5882 59.4062 55.7271 61.1765 52.2868 63.1998L50.2953 64.3526Z"
                    fill="#CC00FF"
                  />
                </g>
              </g>
            </svg>
          </div>
        </div>
        <a
          :href="product.topMerchant.urlWithSubtag"
          rel="sponsored,nofollow"
          target="_blank"
          v-on-visible="onProductVisible"
          @click="onImageClick"
        >
          <picture>
            <source
              media="(min-resolution: 192dpi) and (min-width: 1180px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 1180px)"
              :srcset="product.imgUrlx2"
              :alt="product.imgAlt"
              width="600"
              height="600"
            />
            <source
              media="(min-width: 1180px)"
              :srcset="product.imgUrlx2"
              :alt="product.imgAlt"
              width="600"
              height="600"
            />
            <source
              media="(min-resolution: 192dpi) and (min-width: 768px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 768px)"
              :srcset="product.imgUrlx2"
              :alt="product.imgAlt"
              width="600"
              height="600"
            />
            <source
              media="(min-width: 768px)"
              :srcset="product.imgUrlx2"
              :alt="product.imgAlt"
              width="600"
              height="600"
            />
            <source
              media="(min-resolution: 192dpi), (-webkit-min-device-pixel-ratio: 2)"
              :srcset="product.imgUrlx2"
              :alt="product.imgAlt"
              width="600"
              height="600"
            />
            <img
              :src="product.imgUrl"
              :alt="product.imgAlt"
              width="600"
              height="600"
              class="product-image"
            />
          </picture>
        </a>
        <div
          class="save-notification-wrapper"
          :style="{ display: showNotification ? 'flex' : 'none' }"
        >
          <div
            class="save-notification"
            :class="{ active: notificationActive }"
            ref="saveNotification"
          >
            <span class="saved-text">
              <img
                src="/media/components/product-grid-item/success-icon.svg"
                width="12"
                height="9"
                aria-hidden="true"
                alt="success icon"
              />
              Saved
            </span>
            <a class="saved-for-later-link" :href="savedForLaterLink">View</a>
          </div>
        </div>
      </div>
      <div class="detail-section">
        <div class="product-name">
          <a
            :href="product.topMerchant.urlWithSubtag"
            target="_blank"
            @click="onNameClick"
          >
            <template v-if="product.friendlyName">
              <span class="product-friendly-name">{{
                product.friendlyName
              }}</span>
              <span class="product-name-glyph"> ⚹</span>
            </template>
            {{ product.name }}
          </a>
        </div>
        <div class="product-pricing">
          <div class="price">
            {{
              '$' +
              (product.topMerchant.isOnSale
                ? product.topMerchant.roundedSalePrice
                : product.topMerchant.roundedPrice)
            }}
          </div>
          <div class="sale-info" v-if="product.topMerchant.isOnSale">
            <div class="list-price">
              {{ '$' + product.topMerchant.roundedPrice }}
            </div>
            <div class="off">{{ product.topMerchant.salePercentage }}% off</div>
          </div>
        </div>
      </div>
      <div class="buy-section">
        <div class="buy-item">
          <a
            :href="product.topMerchant.urlWithSubtag"
            class="buy-link"
            target="_blank"
            @click="onBuyClick"
          >
            <div class="buy-button">
              Buy at {{ product.topMerchant.retailerName }}
            </div>
          </a>
          <div
            class="offer-code"
            v-if="product.topMerchant.isOnSale && product.topMerchant.offerCode"
          >
            with code: {{ product.topMerchant.offerCode }}
          </div>
        </div>
      </div>
    </div>
    <div class="read-more">
      <a
        class="read-more-link"
        :href="readStoryUrl"
        target="_blank"
        @click="onReadStoryClick"
        >Read Story</a
      >
    </div>
  </div>
</template>

<style scoped lang="postcss"></style>
